import { Component, Vue, Watch } from 'vue-property-decorator';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import template from './Invoices.vue';
import { invoiceLabels, invoiceColors, InvoiceStatus } from '@/_helpers/enums';
import 'dayjs/locale/nl';
import invoice from '@/store/modules/invoice';
import store from '@/store';
import { Invoice } from '@/store/models/invoice';
import { Meta } from '@/store/models/response';
import { Query } from '@/store/models';
import auth from '@/store/modules/auth';
import { router } from '@/_helpers';

dayjs.extend(LocalizedFormat);

dayjs.locale('nl');

@Component({
	mixins: [template],
	filters: {
		price(priceInCents: number): string {
			let price = `${Math.floor(priceInCents / 100)}`;
			if (priceInCents % 100 === 0) {
				price += ',-';
			} else {
				price += `,${priceInCents % 100}`;
			}

			return price;
		},
		currency(currency: string): string {
			// TODO add other currencies;
			if (currency === 'eur') {
				return '€';
			}
			return '€';
		}
	}
})
export default class Invoices extends Vue {
	headers: any = [];

	footerProps: any = {};

	options: any = {};

	pagination: any = {};

	loading: boolean = false;

	serverItems: number = 0;

	get invoices(): Invoice[] {
		return invoice.invoices;
	}

	get meta(): Meta | null {
		return invoice.meta ? invoice.meta : null;
	}

	getLabelText(status: InvoiceStatus): string {
		return invoiceLabels[status] || 'Onbekend';
	}

	getLabelColor(status: InvoiceStatus): string {
		return invoiceColors[status] || 'blue-grey';
	}

	formatDate(date: string): string {
		return dayjs(date).format('ll') || '-';
	}

	async mounted(): Promise<void> {
		// Load the options of the v-data-table
		let page = 1;
		let perPage = 10;

		if (this.$route.query.page !== undefined) {
			page = parseInt(this.$route.query.page.toString(), 10);
		}

		if (this.$route.query.perPage !== undefined) {
			perPage = parseInt(this.$route.query.perPage.toString(), 10);
		}

		this.options = {
			page,
			itemsPerPage: perPage,
			sortBy: [],
			sortDesc: [],
			groupBy: [],
			groupDesc: [],
			multiSort: false,
			mustSort: false
		};

		// Load the headers into the v-data-table, turn of all sorting
		this.headers = [
			{ text: 'Nummer', value: 'number', align: 'start', sortable: false },
			{ text: 'Datum', value: 'date', sortable: false },
			{ text: 'Bedrag', value: 'amount', sortable: false },
			{ text: 'Status', value: 'status', sortable: false },
			{ text: '', value: 'download_url', sortable: false },
			{ text: '', value: 'view_url', sortable: false }
		];

		// Load all footerprops into the v-data-table
		this.footerProps = {
			'items-per-page-options': [5, 10, 20],
			'items-per-page-text': 'Items per pagina:',
			'page-text': '',
			pagination: {
				page,
				itemsPerPage: perPage,
				pageCount: 2,
				itemsLength: 6
			}
		};

		// Get self
		try {
			await auth.getSelf(false);
		} catch (e) {
			// If an error occurs, it has no point to try to get the invoices. Stop it here and show a message.
			store.commit('openNotifier', {
				text: `Er ging wat fout tijdens het ophalen van de facturen`,
				color: 'error'
			});
			return;
		}

		// Create query
		const query: Query = {
			query: {
				perPage: this.$route.query.perPage,
				previousLastId: this.$route.query.previousLastId,
				previousFirstId: this.$route.query.previousFirstId
			},
			customerID: auth.self.customerId
		};

		// Get the invoices based on the query
		await this.get(query);
	}

	async get(query: Query): Promise<void> {
		this.loading = true;

		try {
			await invoice.getAll(query);
			// Set amount of server items by calculating items per page * amount of pages + amount that just got loaded in
			this.serverItems = this.options.itemsPerPage * (this.options.page - 1) + invoice.invoices.length;

			// Set amount of pages to current page
			this.footerProps.pagination.pageCount = this.options.page;
			if (invoice.meta.lastId) {
				// increase server items and page count if last id is set, doing this will trick the v-data-table into thinking there is another page
				this.serverItems += 1;
				this.footerProps.pagination.pageCount += 1;
			}
			// Set the pagination items length to trick the v-data-table into thinking there is another page
			this.footerProps.pagination.itemsLength = this.serverItems;
		} catch (error) {
			store.commit('openNotifier', { text: `Er ging wat fout tijdens het ophalen van de facturen`, color: 'error' });
		} finally {
			this.loading = false;
		}
	}

	@Watch('options')
	onChange(): void {
		// Check if the options have been changed, otherwise we will get an infinite loop
		if (!this.optionsChanged()) {
			return;
		}
		// Build query based on thinks changed in the options from the v-data-table
		const query: any = {
			perPage: this.options.itemsPerPage,
			page: this.options.page
		};

		// Set the last or first id based on if the page is before or after current page
		if (this.options.page > this.$route.query.page) {
			query.previousLastId = this.meta?.lastId;
		} else if (this.options.page < this.$route.query.page) {
			query.previousFirstId = this.meta?.firstId;
		}

		// push to new url
		router.push({ query });
	}

	optionsChanged(): boolean {
		let changed = false;
		// check if any of the query values have been changed
		if (this.$route.query.perPage !== undefined && this.options.itemsPerPage !== undefined) {
			if (this.$route.query.perPage.toString() !== this.options.itemsPerPage.toString()) {
				changed = true;
			}
		} else {
			changed = true;
		}
		if (this.$route.query.page !== undefined && this.options.page !== undefined) {
			if (this.$route.query.page.toString() !== this.options.page.toString()) {
				changed = true;
			}
		} else {
			changed = true;
		}
		return changed;
	}
}
